import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Agent Skills (al-tutor migration 183, PRD agent-skills). A skill is a
 * procedure in the Claude Skills format — SKILL.md plus text reference
 * files — assigned to agents. Imports arrive inactive.
 */

export interface SkillFile {
  id: string;
  rel_path: string;
  content_type: string;
  size_bytes: number;
  is_executable_stub: boolean;
}

export interface SkillImportReport {
  accepted: string[];
  rejected: { path: string; reason: string }[];
  scripts: string[];
  warnings: string[];
  /** secrets the skill says it needs (e.g. ADANOS_API_KEY) */
  credentials?: string[];
}

export interface Skill {
  id: string;
  name: string;
  title?: string | null;
  description: string;
  body_md?: string | null;
  frontmatter: Record<string, any>;
  version: number;
  source: 'manual' | 'import';
  origin_filename?: string | null;
  /** GitHub origin (migration 190) */
  origin_url?: string | null;
  origin_ref?: string | null;
  origin_license?: string | null;
  origin_library?: string | null;
  import_report?: SkillImportReport | null;
  is_active: boolean;
  load_count: number;
  last_loaded_at?: string | null;
  files: SkillFile[];
  assigned_bot_ids: string[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SkillInput {
  name?: string;
  title?: string | null;
  description?: string;
  body_md?: string;
  is_active?: boolean;
}

/** A skill in the library, or one found at a pasted GitHub link. */
export interface LibrarySkill {
  source: string;
  path: string;
  name: string;
  description: string;
  license?: string | null;
  /** false = its license does not allow copying it into the account */
  importable: boolean;
  /** ships scripts; our agents run no code, so only the text part works */
  needs_code: boolean;
  url: string;
  commit: string;
  score?: number | null;
  files: string[];
  skipped_files: number;
}

export interface LibrarySource {
  id: string;
  label: string;
  repo_url: string;
  count?: number;
  importable?: number;
  indexed_at?: string;
  commit?: string;
}

export interface LibraryQuery {
  q?: string;
  source?: string | null;
  codeFree?: boolean;
  limit?: number;
  offset?: number;
}

/** The import and library dialogs show errors themselves — no global toast. */
const QUIET = { headers: new HttpHeaders({ 'X-Suppress-Error-Toast': '1' }) };

/**
 * A readable sentence for a failed import / library call. The backend sends
 * `{code, message, …}` (skill_library.py) — translated per code under
 * pages.skills.errors.codes; a plain string detail is shown as the reason.
 */
export function skillErrorText(err: HttpErrorResponse | any, t: TranslateService): string {
  const detail = err?.error?.detail;
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
    const key = `pages.skills.errors.codes.${detail.code}`;
    const text = t.instant(key, detail);
    return text !== key ? text : detail.message || t.instant('pages.skills.errors.saveFailed');
  }
  if (typeof detail === 'string' && detail) {
    return t.instant('pages.skills.errors.withReason', { reason: detail });
  }
  if (err?.status === 0) return t.instant('pages.skills.errors.codes.github_unreachable');
  return t.instant('pages.skills.errors.saveFailed');
}

/** Keep in sync with al-tutor (models.py). */
export const SKILL_DESCRIPTION_MAX = 1024;
export const SKILLS_MAX_PER_BOT = 20;

@Injectable({ providedIn: 'root' })
export class SkillService {
  private readonly base = `${environment.tutorsBackend}/skills`;

  constructor(private http: HttpClient) {}

  list(): Observable<Skill[]> {
    return this.http.get<{ data: Skill[] }>(this.base).pipe(map((r) => r.data));
  }

  get(id: string): Observable<Skill> {
    return this.http.get<Skill>(`${this.base}/${id}`);
  }

  create(input: SkillInput): Observable<Skill> {
    return this.http.post<Skill>(this.base, input);
  }

  update(id: string, input: SkillInput): Observable<Skill> {
    return this.http.put<Skill>(`${this.base}/${id}`, input);
  }

  delete(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/${id}`);
  }

  import(file: File): Observable<Skill> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<Skill>(`${this.base}/import`, form, QUIET);
  }

  importFromGithub(url: string, paths?: string[]): Observable<{ imported: Skill[]; candidates: LibrarySkill[] }> {
    return this.http.post<{ imported: Skill[]; candidates: LibrarySkill[] }>(
      `${this.base}/import-github`,
      { url, paths: paths ?? null },
      QUIET
    );
  }

  // ── Library ──

  librarySources(): Observable<LibrarySource[]> {
    return this.http.get<{ data: LibrarySource[] }>(`${this.base}/library/sources`).pipe(map((r) => r.data));
  }

  searchLibrary(query: LibraryQuery): Observable<LibrarySkill[]> {
    const params: Record<string, string> = {
      limit: String(query.limit ?? 30),
      offset: String(query.offset ?? 0),
    };
    if (query.q?.trim()) params['q'] = query.q.trim();
    if (query.source) params['source'] = query.source;
    if (query.codeFree) params['code_free'] = 'true';
    return this.http
      .get<{ data: LibrarySkill[] }>(`${this.base}/library`, { params, ...QUIET })
      .pipe(map((r) => r.data));
  }

  importFromLibrary(source: string, path: string): Observable<Skill> {
    return this.http.post<Skill>(`${this.base}/library/import`, { source, path }, QUIET);
  }

  getFile(skillId: string, fileId: string): Observable<SkillFile & { content: string }> {
    return this.http.get<SkillFile & { content: string }>(`${this.base}/${skillId}/files/${fileId}`);
  }

  putFile(skillId: string, relPath: string, content: string): Observable<Skill> {
    return this.http.post<Skill>(`${this.base}/${skillId}/files`, { rel_path: relPath, content });
  }

  deleteFile(skillId: string, fileId: string): Observable<Skill> {
    return this.http.delete<Skill>(`${this.base}/${skillId}/files/${fileId}`);
  }

  setBots(skillId: string, botIds: string[]): Observable<Skill> {
    return this.http.put<Skill>(`${this.base}/${skillId}/bots`, { bot_ids: botIds });
  }

  // ── Agent side ──

  listForBot(tutorId: string): Observable<Skill[]> {
    return this.http
      .get<{ data: Skill[] }>(`${environment.tutorsBackend}/tutor/${tutorId}/skills`)
      .pipe(map((r) => r.data));
  }

  assignToBot(tutorId: string, skillIds: string[]): Observable<Skill[]> {
    return this.http
      .put<{ data: Skill[] }>(`${environment.tutorsBackend}/tutor/${tutorId}/skills`, { skill_ids: skillIds })
      .pipe(map((r) => r.data));
  }
}
